import React, { useState, useRef, useEffect, ChangeEvent, FormEvent, CSSProperties } from 'react';
import { useTournament } from '@shootout-comps/core';
import { useAuth } from '@shootout-comps/core';
import ConfirmModal from '../components/ConfirmModal';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';
import { resizeImageToDataUrl } from '../utils/resizeImage';

const btn = (extra: CSSProperties = {}): CSSProperties => ({
  border: 'none', borderRadius: '6px', padding: '0.5rem 1rem',
  fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', ...extra,
});

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

interface HistoricPlayer { _id: string; name: string; profilePictureUrl?: string; handicap?: number; }

interface PlayersPageProps {
  tournamentId: string;
}

export default function PlayersPage({ tournamentId }: PlayersPageProps) {
  const { tournament, fetchTournament, addPlayer, removePlayer, startTournament, error } = useTournament();
  const { getAccessToken } = useAuth();
  const { t } = useLang();

  useEffect(() => { fetchTournament(tournamentId); }, [tournamentId]); // eslint-disable-line

  const [name, setName]                     = useState('');
  const [pictureFile, setPictureFile]         = useState<File | null>(null);
  const [picturePreview, setPicturePreview]   = useState<string | null>(null);
  const [existingPicUrl, setExistingPicUrl]   = useState<string | undefined>(undefined);
  const [existingPlayerId, setExistingPlayerId] = useState<string | undefined>(undefined);
  const [handicap, setHandicap]               = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [localError, setLocalError]         = useState('');
  const [showStartConfirm, setShowStartConfirm] = useState(false);

  // Autocomplete state
  const [historicPlayers, setHistoricPlayers] = useState<HistoricPlayer[]>([]);
  const [suggestions, setSuggestions]         = useState<HistoricPlayer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx]   = useState(-1);
  const inputRef    = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const suggestRef  = useRef<HTMLDivElement>(null);

  // Fetch historic players once on mount
  useEffect(() => {
    getAccessToken().then(token => {
      if (!token) return;
      fetch('/api/players/history', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then((data: HistoricPlayer[]) => setHistoricPlayers(data))
        .catch(() => null);
    });
  }, [getAccessToken]);

  // Filter suggestions whenever name changes
  useEffect(() => {
    const q = name.trim().toLowerCase();
    if (q.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
    const currentNames = new Set((tournament?.players ?? []).map(p => p.name.toLowerCase()));
    const matches = historicPlayers.filter(p =>
      p.name.toLowerCase().includes(q) && !currentNames.has(p.name.toLowerCase())
    );
    setSuggestions(matches);
    setShowSuggestions(matches.length > 0);
    setHighlightedIdx(-1);
  }, [name, historicPlayers, tournament?.players]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Guard against briefly rendering a previously-open tournament's stale data while the
  // fetch for this one is still in flight.
  if (!tournament || tournament._id !== tournamentId) return null;

  const maxP = tournament.maxPlayers ?? 32;
  const isHandicap = tournament.isHandicap ?? false;
  const players = [...(tournament.players ?? [])].filter(p => !p.isBye).sort((a, b) => a.seed - b.seed);
  const isRegistration = tournament.status === 'registration';

  const selectSuggestion = (p: HistoricPlayer) => {
    setName(p.name);
    setShowSuggestions(false);
    setHighlightedIdx(-1);
    setExistingPlayerId(p._id);
    // Load the historic player's picture as the preview
    if (p.profilePictureUrl) {
      setPicturePreview(p.profilePictureUrl);
      setExistingPicUrl(p.profilePictureUrl);
      setPictureFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      setPicturePreview(null);
      setExistingPicUrl(undefined);
    }
    // Pre-fill handicap from previous tournament
    if (p.handicap !== undefined) {
      setHandicap(String(p.handicap));
    }
    inputRef.current?.focus();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setPictureFile(null);
      setPicturePreview(null);
      setExistingPicUrl(undefined);
      return;
    }
    // Resize client-side before storing — keeps MongoDB document sizes small
    resizeImageToDataUrl(file).then(dataUrl => {
      setPictureFile(null);          // no raw File needed — we have the data URL
      setExistingPicUrl(dataUrl);    // send as existingPictureUrl to the API
      setPicturePreview(dataUrl);
      setExistingPlayerId(undefined); // fresh photo, not reusing an existing player doc photo
    }).catch(() => {
      // Fallback: use FileReader without resize
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPictureFile(null);
        setExistingPicUrl(ev.target?.result as string);
        setPicturePreview(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setShowSuggestions(false);
    setSubmitting(true); setLocalError('');
    try {
      const hVal = handicap !== '' ? Number(handicap) : undefined;
      await addPlayer(tournamentId, name.trim(), undefined, existingPicUrl, hVal !== undefined && !isNaN(hVal) ? hVal : undefined, existingPlayerId);
      setName(''); setPictureFile(null); setPicturePreview(null); setExistingPicUrl(undefined); setExistingPlayerId(undefined); setHandicap('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to add player');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && highlightedIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[highlightedIdx]); }
    else if (e.key === 'Escape') { setShowSuggestions(false); setHighlightedIdx(-1); }
  };

  const handleStartConfirmed = async () => {
    setShowStartConfirm(false);
    try { await startTournament(tournamentId); }
    catch (err) { setLocalError(err instanceof Error ? err.message : 'Failed to start'); }
  };

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      {showStartConfirm && (
        <ConfirmModal
          title="Start Tournament"
          message={t.startTournamentConfirm}
          confirmLabel="🏆 Start Tournament"
          cancelLabel="Cancel"
          onConfirm={handleStartConfirmed}
          onCancel={() => setShowStartConfirm(false)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '2.5rem', color: 'var(--gold)' }}>Players</h2>
        <span style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem' }}>{players.length} / {maxP} {t.registered}</span>
      </div>

      {/* Capacity bar */}
      <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', marginBottom: '2rem', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(players.length / maxP) * 100}%`, background: 'linear-gradient(90deg, var(--gold), var(--gold-light))', borderRadius: '3px', transition: 'width 0.4s ease' }} />
      </div>

      {/* Add player form */}
      {isRegistration && players.length < maxP && (
        <form onSubmit={handleAdd} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            {/* Avatar */}
            <div onClick={() => fileInputRef.current?.click()} title="Add profile picture"
              style={{ width: '56px', height: '56px', borderRadius: '50%', flexShrink: 0, background: 'rgba(0,0,0,0.3)', border: '2px dashed rgba(201,168,76,0.4)', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {picturePreview ? <img src={picturePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '1.4rem' }}>📷</span>}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

            {/* Name input with autocomplete */}
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                ref={inputRef}
                value={name}
                onChange={e => { setName(e.target.value); setExistingPlayerId(undefined); }}
                onKeyDown={handleNameKeyDown}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                placeholder="Player name…"
                autoComplete="off"
                style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', border: '2px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
              />

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestRef} style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'linear-gradient(135deg, #1a3d2a, #1e4530)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '8px', zIndex: 100, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: '200px', overflowY: 'auto' }}>
                  {suggestions.map((p, i) => (
                    <div
                      key={p.name}
                      onMouseDown={e => { e.preventDefault(); selectSuggestion(p); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.85rem', cursor: 'pointer', background: i === highlightedIdx ? 'rgba(201,168,76,0.15)' : 'transparent', borderBottom: i < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', transition: 'background 0.1s' }}
                      onMouseEnter={() => setHighlightedIdx(i)}
                    >
                      {/* Mini avatar */}
                      <div style={{ width: '26px', height: '26px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '1px solid rgba(201,168,76,0.3)' }}>
                        <img src={p.profilePictureUrl ? p.profilePictureUrl : PLACEHOLDER} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      {/* Name with match highlighted */}
                      <span style={{ fontSize: '0.88rem', color: 'var(--chalk)' }}>
                        {(() => {
                          const q = name.trim().toLowerCase();
                          const idx = p.name.toLowerCase().indexOf(q);
                          if (idx === -1) return p.name;
                          return <>
                            {p.name.slice(0, idx)}
                            <strong style={{ color: 'var(--gold)' }}>{p.name.slice(idx, idx + q.length)}</strong>
                            {p.name.slice(idx + q.length)}
                          </>;
                        })()}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--chalk-dim)' }}>↵</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" disabled={!name.trim() || submitting}
              style={btn({ background: 'var(--gold)', color: 'var(--baize-dark)', padding: '0.75rem 1.5rem', opacity: !name.trim() || submitting ? 0.5 : 1 })}>
              + Add
            </button>
          </div>
          {isHandicap && (
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', flexShrink: 0 }}>{t.handicapEditHint}:</label>
              <input
                type="number"
                max="147"
                value={handicap}
                onChange={e => setHandicap(e.target.value)}
                placeholder="0"
                style={{ width: '80px', padding: '0.45rem 0.65rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '6px', color: 'var(--chalk)', fontSize: '0.9rem', outline: 'none', textAlign: 'center' }}
              />
              <span style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)' }}>{t.handicapPts}</span>
            </div>
          )}

          {picturePreview && (
            <p style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.5rem', marginLeft: '68px' }}>
              {existingPicUrl ? t.photoFromPrevious : `📎 ${pictureFile?.name}`}{' '}
              — <span style={{ color: 'var(--red)', cursor: 'pointer' }} onClick={() => { setPictureFile(null); setPicturePreview(null); setExistingPicUrl(undefined); if (fileInputRef.current) fileInputRef.current.value = ''; }}>remove</span>
            </p>
          )}

          {historicPlayers.length > 0 && name.trim() === '' && (
            <p style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginTop: '0.6rem', marginLeft: '68px' }}>
              💡 {t.startTypingPlayers}
            </p>
          )}
        </form>
      )}

      {(localError || error) && (
        <div style={{ background: 'rgba(192,57,43,0.2)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.75rem 1rem', color: '#e74c3c', fontSize: '0.85rem', marginBottom: '1rem' }}>
          {localError || error}
        </div>
      )}

      {/* Player list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {players.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '3rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
            <div style={{ marginBottom: '0.5rem' }}><SnookerBallSpinner size={32} /></div>
            {t.noPlayersRegistered}
          </div>
        )}
        {players.map((player, i) => (
          <div key={player._id} style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.6rem 1rem', animation: `fadeIn 0.3s ease ${i * 0.03}s both` }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '2px solid rgba(201,168,76,0.3)', background: '#1a4a2e' }}>
              <img src={player.profilePictureUrl ? player.profilePictureUrl : PLACEHOLDER} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <span style={{ flex: 1, fontWeight: 500 }}>{player.name}</span>
            {isHandicap && (
              <span style={{ fontSize: '0.7rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '4px', padding: '0.15rem 0.45rem', flexShrink: 0 }}>
                H: {(player as unknown as { handicap?: number }).handicap ?? 0}
              </span>
            )}
            {isRegistration && (
              <button onClick={() => removePlayer(tournamentId, player._id)} style={btn({ background: 'rgba(192,57,43,0.15)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', padding: '0.3rem 0.7rem', fontSize: '0.75rem' })}>Remove</button>
            )}
          </div>
        ))}
      </div>

      {/* Start button */}
      {isRegistration && players.length >= 2 && (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button onClick={() => setShowStartConfirm(true)} style={btn({ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', padding: '1rem 3rem', fontSize: '1.1rem', letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: '10px', boxShadow: '0 4px 20px rgba(201,168,76,0.3)' })}>
            🏆 Start Tournament
          </button>
          {players.length < maxP && (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginTop: '0.75rem' }}>
              {maxP - players.length} {t.slotsRemaining} — {t.byesAdded.replace('{n}', String(maxP - players.length))}
            </p>
          )}
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}
